import { observer } from "mobx-react-lite";
import { Center } from "@chakra-ui/react";

import { allExpandTypes, allPivots, APIOption, Dictionary, Lang } from "@noli/core";
import { apiSchema } from "@noli/svg";
import {
  ICommonNoliFrameUIInfoResponse,
  langStore,
  NoliFramePresetTypes,
  translate,
  useSelectingUIInfo,
  Words,
} from "@noli/business";

import getInputProps from "@/styles/inputProps";
import { ISubPanel } from "@/components/SubPanel";
import SubEditorPanel from "@/components/SubPanel/SubEditor";
import SubPanelWithScrollBar from "@/components/SubPanel/WithScrollBar";
import Property from "@/components/Property";
import Select from "@/components/Select";
import SchemaProperty from "@/components/Property/SchemaProperty";
import { NoliFrameLayersPanel } from "./common";

type NoliFrameEditorProps = ISubPanel & { lang: Lang; res: ICommonNoliFrameUIInfoResponse };

function BasicNoliFrameEditor({
  lang,
  res: { displayNode, uiInfo, setContent, setVertical, setExpandType, setPivotType },
  ...others
}: NoliFrameEditorProps) {
  const padding = "10px";
  const commonProps = { w: "40%", h: "32px", mx: "5%", mt: "6px" };
  const { content, vertical, canVertical, expandType, pivotType } = uiInfo;
  const translate_ = (option: string) => translate(option, lang);
  return (
    <SubEditorPanel panelTitle="基础编辑" {...others}>
      <Property
        identifier={`${displayNode.alias}-content`}
        {...commonProps}
        w="90%"
        captionW="0px"
        inputPadding={padding}
        text=""
        value={content}
        setValue={setContent({ trace: true })}
      />
      <Select
        {...commonProps}
        pl={padding}
        iconProps={{ pr: padding }}
        value={translate_(expandType)}
        options={allExpandTypes}
        optionConverter={translate_}
        onOptionClick={setExpandType({ trace: true })}
      />
      <Select
        {...commonProps}
        pl={padding}
        iconProps={{ pr: padding }}
        value={translate_(pivotType)}
        options={allPivots}
        optionConverter={translate_}
        onOptionClick={setPivotType({ trace: true })}
      />
      <SchemaProperty
        identifier={`${displayNode.alias}-vertical-align`}
        apiKey="vertical-align"
        apiOption={new APIOption("bool", vertical)}
        value={vertical}
        setValue={setVertical}
        booleanDisabled={!canVertical}
        {...commonProps}
        pl={padding}
        {...getInputProps()}
        lang={lang}
      />
    </SubEditorPanel>
  );
}
function SpecialNoliFrameEditor({
  lang,
  res: { displayNode, uiInfo, setSchemaProperty },
  ...others
}: NoliFrameEditorProps) {
  const describe = uiInfo.describe;
  const commonProps = { w: "85%", h: "32px", mt: "6px" };
  const schema = apiSchema.noliFrame.allPacks[uiInfo.type];
  const components = specialAPIKeys[describe].map((key) => {
    const apiOption = schema[key];
    return (
      <Center key={key}>
        <SchemaProperty
          identifier={`${displayNode.alias}-${key}`}
          {...commonProps}
          apiKey={key}
          apiOption={apiOption}
          value={(uiInfo.params as any)[key]}
          setValue={setSchemaProperty}
          dictionary={specialDictionaries[describe]}
          selectProps={{
            px: "16px",
          }}
          lang={lang}
        />
      </Center>
    );
  });
  const describeWord = describe === "oneCharOneFrame" ? describe : `${describe}-frame`;
  return (
    <SubEditorPanel
      panelTitle={`特殊编辑 (${translate(describeWord, lang)})`}
      pb="12px"
      maxH="240px"
      {...others}
    >
      {components}
    </SubEditorPanel>
  );
}

function NoliFrameEditor() {
  const lang = langStore.tgt;
  const res = useSelectingUIInfo();
  if (!res) {
    return null;
  }
  if (res.type !== "noliFrame") {
    return null;
  }
  return (
    <SubPanelWithScrollBar w="100%" h="100%">
      <BasicNoliFrameEditor lang={lang} res={res} h="180px" />
      <SpecialNoliFrameEditor lang={lang} res={res} />
      <NoliFrameLayersPanel lang={lang} res={res} w="100%" flex={1} />
    </SubPanelWithScrollBar>
  );
}

export default observer(NoliFrameEditor);

const connectKeys = ["lineH", "align", "gap", "bleedingX", "bleedingY", "offset", "angle"];
const specialAPIKeys: Record<NoliFramePresetTypes, string[]> = {
  button: connectKeys.concat(["leftUpRad", "rightUpRad", "rightDownRad", "leftDownRad"]),
  connect: connectKeys,
  oneCharOneFrame: ["overlay", "gap"],
  other: [],
  noliFrame: [],
};
const specialDictionaries: Record<NoliFramePresetTypes, Dictionary<Words>> = {
  button: {
    gap: "font-gap",
    offset: "offset-distance",
    angle: "offset-angle",
  },
  connect: {
    gap: "font-gap",
    offset: "offset-distance",
    angle: "offset-angle",
  },
  oneCharOneFrame: {
    gap: "font-gap",
  },
  other: {},
  noliFrame: {},
};
